import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, BookOpen, Gift } from 'lucide-react';
import AboutBookModal from './AboutBookModal';
import { BACKEND_URL } from '@/config/api';

const ReadFreeDropdown = ({ onAddToLibrary, onReadNow, isExpanded = false, onToggleExpand }) => {
  const [books, setBooks] = useState([]);
  const [selectedBook, setSelectedBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [booksPerRow, setBooksPerRow] = useState(8);

  // Calculate books per row based on screen size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setBooksPerRow(4);
      } else if (window.innerWidth < 1024) {
        setBooksPerRow(6);
      } else {
        setBooksPerRow(8);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchPromotionalBooks();
  }, []);

  const fetchPromotionalBooks = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${BACKEND_URL}/api/reader/promotional-books`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setBooks(data || []);
      }
    } catch (error) {
      console.error('Error fetching promotional books:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = () => {
    if (onToggleExpand) {
      onToggleExpand();
    }
  };

  const displayedBooks = isExpanded ? books : books.slice(0, booksPerRow);
  const hasMore = books.length > booksPerRow;
  const remainingCount = books.length - booksPerRow;

  if (loading) {
    return (
      <section className="rounded-2xl bg-slate-900/60 p-4 shadow-xl shadow-slate-950/50 ring-1 ring-slate-800">
        <div className="flex items-center gap-2">
          <Gift className="w-5 h-5 text-green-400" />
          <h2 className="text-base font-semibold sm:text-lg">Read Free</h2>
        </div>
        <div className="mt-4 grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-3">
          {[...Array(booksPerRow)].map((_, i) => (
            <div key={i}>
              <div className="aspect-[2/3] w-full animate-pulse rounded-lg bg-slate-800 mb-2" />
              <div className="h-3 w-3/4 animate-pulse rounded bg-slate-800 mb-1" />
              <div className="h-2 w-1/2 animate-pulse rounded bg-slate-800" />
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (!books || books.length === 0) {
    return (
      <section className="rounded-2xl bg-slate-900/60 p-4 shadow-xl shadow-slate-950/50 ring-1 ring-slate-800">
        <div className="flex items-center gap-2">
          <Gift className="w-5 h-5 text-green-400" />
          <h2 className="text-base font-semibold sm:text-lg">Read Free</h2>
        </div>
        <div className="mt-4 rounded-xl bg-slate-950/60 p-6 text-center">
          <BookOpen className="h-10 w-10 mx-auto mb-2 text-slate-600" />
          <p className="text-slate-300 text-sm font-medium mb-1">No promotional books available</p>
          <p className="text-xs text-slate-400">Check back later for free books</p>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="rounded-2xl bg-slate-900/60 p-4 shadow-xl shadow-slate-950/50 ring-1 ring-slate-800">
        {/* Header */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Gift className="w-5 h-5 text-green-400" />
            <h2 className="text-base font-semibold sm:text-lg">Read Free</h2>
            <span className="text-xs text-slate-400 ml-2">
              {books.length} promotional {books.length === 1 ? 'book' : 'books'}
            </span>
          </div>
          
          {hasMore && (
            <button
              onClick={toggleExpand}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm font-medium transition"
              data-testid="read-free-expand-btn"
            >
              {isExpanded ? (
                <>
                  <span>Show Less</span>
                  <ChevronUp className="w-4 h-4" />
                </>
              ) : (
                <>
                  <span>+{remainingCount} More</span>
                  <ChevronDown className="w-4 h-4" />
                </>
              )}
            </button>
          )}
        </div>

        {/* Books Grid */}
        <div className="mt-4">
          <div 
            className={`grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-3 transition-all duration-300 ${
              isExpanded ? 'max-h-none' : 'max-h-[280px] overflow-hidden'
            }`}
          >
            {displayedBooks.map((book) => (
              <div
                key={book.id}
                onClick={() => setSelectedBook(book)}
                className="group cursor-pointer"
              >
                <div className="aspect-[2/3] w-full overflow-hidden rounded-lg bg-slate-800 mb-1.5 ring-1 ring-slate-700 transition group-hover:ring-green-500 group-hover:ring-2 relative">
                  {/* FREE Badge */}
                  <div className="absolute top-1 left-1 z-10 bg-green-600 text-white text-[8px] px-1.5 py-0.5 rounded-full font-bold shadow-lg">
                    FREE
                  </div>
                  
                  {book.cover_url ? (
                    <img
                      src={`${BACKEND_URL}${book.cover_url}`}
                      alt={book.title}
                      className="h-full w-full object-cover transition group-hover:scale-105"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-slate-500">
                      <BookOpen className="h-8 w-8" />
                    </div>
                  )}
                </div>
                <h3 className="font-medium text-slate-50 text-[10px] sm:text-xs line-clamp-2 leading-tight mb-0.5">
                  {book.title}
                </h3>
                <p className="text-[9px] sm:text-[10px] text-slate-400 line-clamp-1">
                  {book.author}
                </p>
              </div>
            ))}
          </div>
          
          {/* Gradient fade when collapsed */}
          {!isExpanded && hasMore && (
            <div className="relative">
              <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-slate-900/90 to-transparent pointer-events-none" />
              <button
                onClick={toggleExpand}
                className="relative z-10 w-full mt-2 py-2 text-center text-sm text-green-400 hover:text-green-300 font-medium transition"
              >
                +{remainingCount} more free books
              </button>
            </div>
          )}
        </div>
      </section>

      {selectedBook && (
        <AboutBookModal
          book={selectedBook}
          isOpen={!!selectedBook}
          onClose={() => setSelectedBook(null)}
          isAuthenticated={true}
          onAddToLibrary={onAddToLibrary}
          onReadNow={onReadNow}
        />
      )}
    </>
  );
};

export default ReadFreeDropdown;
